import * as Sentry from "@sentry/vue";
import * as v from "valibot";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  shallowRef,
} from "vue";

import { defineStore } from "@/lib/depot";
import { useLocalStorageRef } from "@/lib/composables/local-storage";
import { useMediaQuery } from "@/lib/media_query";
import type { RouteToConfiguration } from "@/search/routes";

import { useAccountStore } from "./account";

const previewContext = v.picklist([
  "archive",
  "email",
  "portal",
  "subscribe",
  "web",
]);

export type PreviewContext = v.InferOutput<typeof previewContext>;

const bookmarkItem = v.object({
  name: v.string(),
  path: v.optional(v.string()),
  label: v.optional(v.string()),
});

type BookmarkItem = v.InferOutput<typeof bookmarkItem>;

const draftRecipientItem = v.variant("type", [
  v.object({
    type: v.literal("custom"),
    id: v.string(),
    email: v.string(),
  }),
  v.object({
    type: v.literal("subscribers"),
    id: v.string(),
    email: v.string(),
  }),
]);

type DraftRecipientItem = v.InferOutput<typeof draftRecipientItem>;

const editorMode = v.picklist(["fancy", "plaintext"]);

type PersistedEditorMode = v.InferOutput<typeof editorMode>;

const metricsPeriod = v.picklist(["week", "month", "year", "day"]);

export type MetricsPeriod = v.InferOutput<typeof metricsPeriod>;

const homeActivityBlock = v.picklist([
  "shortcuts",
  "drilldown-metrics",
  "recent-writing",
  "todo",
  "your-activity",
  "whats-happening",
  "failed-api-requests",
]);

export type HomeActivityBlock = v.InferOutput<typeof homeActivityBlock>;

const accountState = v.object({
  newsletterOrder: v.optional(v.array(v.string()), []),
  showEmptyInvoices: v.optional(v.boolean()),
});

type AccountState = v.InferOutput<typeof accountState>;

const newsletterState = v.object({
  analyticsPeriod: v.optional(metricsPeriod),
  bookmarks: v.optional(v.array(bookmarkItem), []),
  draftRecipients: v.optional(v.array(draftRecipientItem), []),
  drilldownMetricsPeriod: v.optional(metricsPeriod),
  editorMode: v.optional(editorMode),
  homeActivityBlocks: v.optional(v.array(homeActivityBlock)),
  panels: v.optional(v.record(v.string(), v.array(v.string())), {}),
  previewContext: v.optional(previewContext),
  showEmailPreview: v.optional(v.boolean()),
  subscriberDetailActivityExclude: v.optional(v.array(v.string())),
  whatsHappeningEventTypeExclude: v.optional(v.array(v.string())),
  yourActivityEventTypeExclude: v.optional(v.array(v.string())),
});

type NewsletterState = v.InferOutput<typeof newsletterState>;

const stateSchema = v.object({
  version: v.literal(1),
  theme: v.optional(v.picklist(["auto", "light", "dark"]), "auto"),
  showDesktopSidebar: v.optional(v.boolean(), true),
  accounts: v.optional(v.record(v.string(), accountState), {}),
  newsletters: v.optional(v.record(v.string(), newsletterState), {}),
});

type State = v.InferOutput<typeof stateSchema>;

const getDefaultState = (): State => {
  return {
    version: 1,
    theme: "auto",
    showDesktopSidebar: true,
    accounts: {},
    newsletters: {},
  };
};

const getDefaultAccountState = (): AccountState => {
  return {
    newsletterOrder: [],
    showEmptyInvoices: undefined,
  };
};

const getDefaultNewsletterState = (): NewsletterState => {
  return {
    analyticsPeriod: undefined,
    bookmarks: [],
    draftRecipients: [],
    drilldownMetricsPeriod: undefined,
    editorMode: undefined,
    homeActivityBlocks: undefined,
    panels: {},
    previewContext: undefined,
    showEmailPreview: undefined,
    subscriberDetailActivityExclude: undefined,
    whatsHappeningEventTypeExclude: undefined,
    yourActivityEventTypeExclude: undefined,
  };
};

const cleanState = (state: State): v.InferInput<typeof stateSchema> => {
  const accounts: Record<string, v.InferInput<typeof accountState>> = {};
  const newsletters: Record<string, v.InferInput<typeof newsletterState>> = {};

  for (const [id, sub] of Object.entries(state.accounts)) {
    const cleaned: v.InferInput<typeof accountState> = {};

    if (sub.newsletterOrder.length > 0) {
      cleaned.newsletterOrder = sub.newsletterOrder;
    }
    if (sub.showEmptyInvoices !== undefined) {
      cleaned.showEmptyInvoices = sub.showEmptyInvoices;
    }

    if (Object.keys(cleaned).length > 0) {
      accounts[id] = cleaned;
    }
  }

  for (const [id, sub] of Object.entries(state.newsletters)) {
    const cleaned: v.InferInput<typeof newsletterState> = {};

    if (sub.analyticsPeriod !== undefined) {
      cleaned.analyticsPeriod = sub.analyticsPeriod;
    }
    if (sub.bookmarks?.length > 0) {
      cleaned.bookmarks = sub.bookmarks;
    }
    if (sub.draftRecipients?.length > 0) {
      cleaned.draftRecipients = sub.draftRecipients;
    }
    if (sub.drilldownMetricsPeriod !== undefined) {
      cleaned.drilldownMetricsPeriod = sub.drilldownMetricsPeriod;
    }
    if (sub.editorMode !== undefined) {
      cleaned.editorMode = sub.editorMode;
    }
    if (sub.homeActivityBlocks !== undefined) {
      cleaned.homeActivityBlocks = sub.homeActivityBlocks;
    }
    if (sub.panels && Object.keys(sub.panels).length > 0) {
      cleaned.panels = sub.panels;
    }
    if (sub.previewContext !== undefined) {
      cleaned.previewContext = sub.previewContext;
    }
    if (sub.subscriberDetailActivityExclude) {
      cleaned.subscriberDetailActivityExclude =
        sub.subscriberDetailActivityExclude;
    }
    if (sub.whatsHappeningEventTypeExclude) {
      cleaned.whatsHappeningEventTypeExclude =
        sub.whatsHappeningEventTypeExclude;
    }
    if (sub.yourActivityEventTypeExclude) {
      cleaned.yourActivityEventTypeExclude = sub.yourActivityEventTypeExclude;
    }

    if (Object.keys(cleaned).length > 0) {
      newsletters[id] = cleaned;
    }
  }

  return {
    ...state,
    accounts: accounts,
    newsletters: newsletters,
  };
};

export const useStore = defineStore("uiStates", () => {
  const accountStore = useAccountStore();

  const state = useLocalStorageRef("pinia:ui-states", {
    parse(raw) {
      if (raw !== null) {
        try {
          const parsed = JSON.parse(raw);
          return v.parse(stateSchema, parsed);
        } catch (err) {
          console.error(err);
          Sentry.captureException(err, {
            fingerprint: ["pinia", "ui-states", "restore-fail"],
          });
        }
      }

      return getDefaultState();
    },
    stringify(value) {
      return JSON.stringify(cleanState(value));
    },
  });

  // App theme
  const appTheme = computed({
    get() {
      return state.value.theme;
    },
    set(next) {
      state.value.theme = next;
    },
  });

  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const appThemeNormalized = computed((): "light" | "dark" => {
    const raw = appTheme.value;

    if (raw === "auto") {
      return !prefersDark.value ? "light" : "dark";
    }

    return raw;
  });

  // Sidebar
  const isDesktopSidebarOpen = computed({
    get() {
      return state.value.showDesktopSidebar;
    },
    set(next) {
      state.value.showDesktopSidebar = next;
    },
  });

  const isMobileSidebarOpen = shallowRef(false);

  // Account store
  const accountState = computed((): AccountState => {
    const account = accountStore.account;

    if (account) {
      const record = state.value.accounts;

      record[account.id] ??= getDefaultAccountState();
      return record[account.id];
    }

    return getDefaultAccountState();
  });

  const newsletterOrder = computed({
    get(): string[] {
      return accountState.value.newsletterOrder;
    },
    set(next) {
      accountState.value.newsletterOrder = next;
    },
  });

  const showEmptyInvoices = computed({
    get(): boolean {
      return accountState.value.showEmptyInvoices ?? false;
    },
    set(next) {
      accountState.value.showEmptyInvoices = next;
    },
  });

  // Newsletter store
  const newsletterState = computed((): NewsletterState => {
    const newsletter = accountStore.newsletter;

    if (newsletter) {
      const record = state.value.newsletters;

      record[newsletter.id] ??= getDefaultNewsletterState();
      return record[newsletter.id];
    }

    return getDefaultNewsletterState();
  });

  const bookmarks = computed({
    get(): BookmarkItem[] {
      const rawBookmarks = newsletterState.value.bookmarks;
      // Migrate old bookmarks that don't have a path field
      return rawBookmarks.map((bookmark) => {
        if (!bookmark.path && bookmark.name) {
          // For old bookmarks, use the route name as the path
          // This will work for routes without query params
          return {
            ...bookmark,
            path: bookmark.name,
          };
        }
        return bookmark;
      });
    },
    set(next) {
      newsletterState.value.bookmarks = next;
    },
  });

  const draftRecipients = computed({
    get(): DraftRecipientItem[] {
      return newsletterState.value.draftRecipients;
    },
    set(next) {
      newsletterState.value.draftRecipients = next;
    },
  });

  const editorMode = computed({
    get(): PersistedEditorMode {
      return newsletterState.value.editorMode ?? "fancy";
    },
    set(next) {
      newsletterState.value.editorMode = next;
    },
  });

  const homeActivityBlocks = computed({
    get(): HomeActivityBlock[] {
      const persisted = newsletterState.value.homeActivityBlocks;

      if (persisted === undefined) {
        return [
          "shortcuts",
          "drilldown-metrics",
          "recent-writing",
          "todo",
          "your-activity",
          "whats-happening",
          "failed-api-requests",
        ];
      }

      return persisted;
    },
    set(next) {
      newsletterState.value.homeActivityBlocks = next;
    },
  });

  const panels = computed({
    get(): Record<string, string[]> {
      return newsletterState.value.panels;
    },
    set(next) {
      newsletterState.value.panels = next;
    },
  });

  const isEmailPreviewOpen = computed({
    get(): boolean {
      return newsletterState.value.showEmailPreview ?? true;
    },
    set(next) {
      newsletterState.value.showEmailPreview = next;
    },
  });

  const previewContextState = computed({
    get(): PreviewContext | undefined {
      return newsletterState.value.previewContext;
    },
    set(next: PreviewContext | undefined) {
      newsletterState.value.previewContext = next;
    },
  });

  const subscriberDetailActivityExclude = computed({
    get(): Readonly<string[]> | undefined {
      return newsletterState.value.subscriberDetailActivityExclude;
    },
    set(next: string[]) {
      newsletterState.value.subscriberDetailActivityExclude = next;
    },
  });

  const analyticsPeriod = computed({
    get(): MetricsPeriod {
      return newsletterState.value.analyticsPeriod ?? "day";
    },
    set(next) {
      newsletterState.value.analyticsPeriod = next;
    },
  });

  const drilldownMetricsPeriod = computed({
    get(): MetricsPeriod {
      return newsletterState.value.drilldownMetricsPeriod ?? "week";
    },
    set(next) {
      newsletterState.value.drilldownMetricsPeriod = next;
    },
  });

  const whatsHappeningEventTypeExclude = computed({
    get(): Readonly<string[]> | undefined {
      return newsletterState.value.whatsHappeningEventTypeExclude;
    },
    set(next: string[]) {
      newsletterState.value.whatsHappeningEventTypeExclude = next;
    },
  });

  const yourActivityEventTypeExclude = computed({
    get(): Readonly<string[]> | undefined {
      return newsletterState.value.yourActivityEventTypeExclude;
    },
    set(next: string[]) {
      newsletterState.value.yourActivityEventTypeExclude = next;
    },
  });

  const contextualSearchRoutes = ref<Partial<RouteToConfiguration>>({});

  return reactive({
    appTheme,
    appThemeNormalized,

    isDesktopSidebarOpen,
    isMobileSidebarOpen,

    newsletterOrder,
    showEmptyInvoices,

    analyticsPeriod,
    bookmarks,
    draftRecipients,
    drilldownMetricsPeriod,
    editorMode,
    homeActivityBlocks,
    isEmailPreviewOpen,
    panels,
    previewContext: previewContextState,
    subscriberDetailActivityExclude,
    whatsHappeningEventTypeExclude,
    yourActivityEventTypeExclude,

    contextualSearchRoutes,
  });
});

export const useContextualSearchRoutes = (
  routes: Partial<RouteToConfiguration>
) => {
  const store = useStore();
  onMounted(() => {
    store.contextualSearchRoutes = routes;
  });
  onBeforeUnmount(() => {
    store.contextualSearchRoutes = {};
  });
};
